// 企業 SSO 的三個頁（plan SPEC #vms-web-sso-ui、#vms-web-sso-flows，cloud/platform#64）：
//   /sso/done       IdP → identity 轉回來：讀 fragment（code / error / r）→ exchange → 回原頁（整頁重新載入，由 cookie 還原登入）
//   /sso/start      IdP 入口圖示用（Entra My Apps / Okta Dashboard 的 Sign-on URL）：不自動跳轉，先顯示要用哪個組織的公司帳號登入
//   /sso/step-up    任何 API 回 401 ORG_SSO_REQUIRED 的 sso_login_url：「〈組織〉要求以公司帳號驗證」；break-glass 名單的人可用 passkey 緊急進入
//   /sso/first-factor  SSO-18：公司帳號建的新帳號還沒有自己的登入方式，碰到要求兩步驟驗證的組織（含委派方要求 SSO → 受託方要 MFA）
//                   或 SSO 登入範圍外的東西時，引導設第一把 passkey → 用它重新驗證、範圍解除 → 回原頁
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { KinioError } from '@kinio/web-sdk';
import { useApp } from '../../lib/AppContext';
import {
  SessionError,
  needsFirstFactor,
  reauthWithPasskey,
  setupFirstPasskey,
  ssoBegin,
  ssoBreakGlass,
  ssoDiscoverOrg,
  ssoExchange,
  ssoScope,
  type SsoDiscovery,
} from '../../lib/session';
import { passkeySupported } from '../../lib/webauthn';
import { markSsoNoAccount, noteSsoLoginOrg, ssoDoneOk, ssoLinkFailed } from '../../lib/pendingSsoLink';
import { EmailVerifyPrompt, emailVerifyRequired } from '../../components/EmailVerifyPrompt';

function codeOf(x: unknown): string {
  return x instanceof SessionError || x instanceof KinioError ? x.code : 'default';
}

/** 409 SSO_NOT_LINKED 且 reason = no_account：這個 IdP 身分還沒連到任何 Kinio 帳號 → 引導先登入 / 註冊（a1-b7-strings 第 5 段，kq Q143）。
 *  email_exists / not_member 照舊顯示 err_SSO_NOT_LINKED。 */
function isNoAccount(x: unknown): boolean {
  return (x instanceof SessionError || x instanceof KinioError) && x.code === 'SSO_NOT_LINKED' && x.detail?.reason === 'no_account';
}

/** 站內相對路徑才收（防開放轉址；BFF 也擋一次）。 */
export function safeReturn(p: string | null): string {
  return p && p.startsWith('/') && !p.startsWith('//') && !p.startsWith('/\\') ? p : '/';
}

const NO_ACCOUNT = 'no_account';

export function SsoDonePage() {
  const { t } = useTranslation();
  const [err, setErr] = useState<string | null>(null);
  const [reminder, setReminder] = useState<string | null>(null);
  const [verify, setVerify] = useState<{ resend: boolean } | null>(null);
  useEffect(() => {
    const f = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    window.history.replaceState(null, '', window.location.pathname); // 交換碼不留在網址列 / 歷史紀錄
    const code = f.get('code');
    const ref = f.get('r') ?? '';
    if (!code) {
      setErr(f.get('error') || 'state');
      return;
    }
    ssoExchange(code, ref)
      .then((r) => {
        ssoDoneOk();
        if (r.email_reminder) setReminder(safeReturn(r.return_path));
        else window.location.replace(safeReturn(r.return_path));
      })
      .catch((x) => {
        if (isNoAccount(x)) markSsoNoAccount(); // 登入後在外框提示〔以公司帳號驗證〕這個組織
        else ssoLinkFailed(codeOf(x));
        // mode = link 時 email 還沒驗證：exchange 回 403 EMAIL_NOT_VERIFIED → 只跳「先驗證你的 email」，不另外顯示錯誤碼（kq Q143、平台審查 F2）
        const ev = emailVerifyRequired(x);
        if (ev) {
          setVerify(ev);
          return;
        }
        setErr(isNoAccount(x) ? NO_ACCOUNT : codeOf(x));
      });
  }, []);
  return (
    <main className="login">
      <div className="card login-card" data-testid="sso-done">
        {!err && !reminder && <p>{t('sso.finishing')}</p>}
        {verify && (
          <>
            <EmailVerifyPrompt resend={verify.resend} onClose={() => setVerify(null)} />
            <a className="btn" href="/">
              {t('sso.back_home')}
            </a>
          </>
        )}
        {err === NO_ACCOUNT && (
          <div data-testid="sso-no-account">
            <h1 className="section-title">{t('sso.no_account_title')}</h1>
            <p>{t('sso.no_account_body')}</p>
            <a className="btn btn-primary" href="/">
              {t('sso.no_account_action')}
            </a>
          </div>
        )}
        {err && err !== NO_ACCOUNT && (
          <>
            <div className="error" role="alert" data-sso-error={err}>
              {t(`sso.err_${err}`, { defaultValue: t('sso.err_default', { code: err }) })}
            </div>
            <a className="btn" href="/">
              {t('sso.back_home')}
            </a>
          </>
        )}
        {reminder && (
          <>
            <p className="notice">{t('sso.email_reminder')}</p>
            <button className="btn btn-primary" type="button" onClick={() => window.location.replace(reminder)}>
              {t('sso.continue')}
            </button>
          </>
        )}
      </div>
    </main>
  );
}

function useOrgSso(orgId: string): [SsoDiscovery | null, boolean] {
  const [d, setD] = useState<SsoDiscovery | null>(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!orgId) {
      setLoaded(true);
      return;
    }
    ssoDiscoverOrg(orgId).then((x) => {
      setD(x);
      setLoaded(true);
    });
  }, [orgId]);
  return [d, loaded];
}

/** /sso/start?org=…：不自動跳轉（防惡意 IdP 讓人靜默登入成別人），按了才開始。 */
export function SsoStartPage() {
  const { t } = useTranslation();
  const { config } = useApp();
  const [q] = useSearchParams();
  const [d, loaded] = useOrgSso(q.get('org') ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function go() {
    if (!d) return;
    setBusy(true);
    try {
      noteSsoLoginOrg(d.org_id);
      window.location.assign(await ssoBegin(d.org_id, 'login', '/'));
    } catch (x) {
      setErr(codeOf(x));
      setBusy(false);
    }
  }
  return (
    <main className="login">
      <div className="card login-card" data-testid="sso-start">
        <div className="login-brand">
          {config.theme.logo && <img src={config.theme.logo} alt="" />}
          <span>{config.display_name}</span>
        </div>
        {loaded && !d && <p className="error">{t('sso.org_unavailable')}</p>}
        {d && (
          <>
            <p>{t('sso.start_body', { org: d.org_display_name, idp: d.idp_display_name })}</p>
            {err && (
              <div className="error" role="alert">
                {t(`sso.err_${err}`, { defaultValue: t('sso.err_default', { code: err }) })}
              </div>
            )}
            <button className="btn btn-primary" type="button" disabled={busy} onClick={() => void go()}>
              {t('sso.sign_in_with', { idp: d.idp_display_name })}
            </button>
          </>
        )}
        <a className="small" href="/">
          {t('sso.use_password')}
        </a>
      </div>
    </main>
  );
}

/** /sso/step-up?org=…&return=…：組織要求以公司帳號驗證（401 ORG_SSO_REQUIRED）。 */
export function SsoStepUpPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [q] = useSearchParams();
  const orgId = q.get('org') ?? '';
  const back = safeReturn(q.get('return'));
  const [d, loaded] = useOrgSso(orgId);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function go(mode: 'step_up') {
    setBusy(true);
    setErr(null);
    try {
      window.location.assign(await ssoBegin(orgId, mode, back));
    } catch (x) {
      setErr(codeOf(x));
      setBusy(false);
    }
  }
  async function breakGlass() {
    setBusy(true);
    setErr(null);
    try {
      await reauthWithPasskey();
      await ssoBreakGlass(orgId);
      nav(back);
    } catch (x) {
      setErr(codeOf(x));
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="page" data-testid="sso-step-up">
      <div className="card" style={{ maxWidth: 560 }}>
        <h1 className="section-title">{t('sso.step_up_title')}</h1>
        {loaded && !d && <p className="error">{t('sso.org_unavailable')}</p>}
        {d && <p>{t('sso.step_up_body', { org: d.org_display_name })}</p>}
        {err && (
          <div className="error" role="alert" data-sso-error={err}>
            {t(`sso.err_${err}`, { defaultValue: t('sso.err_default', { code: err }) })}
          </div>
        )}
        <div className="row-end">
          <Link className="btn" to="/">
            {t('sso.other_orgs')}
          </Link>
          {d && (
            <button className="btn btn-primary" type="button" disabled={busy} onClick={() => void go('step_up')}>
              {t('sso.verify_with', { idp: d.idp_display_name })}
            </button>
          )}
        </div>
        {d && (
          <p className="small muted">
            {t('sso.break_glass_hint')}{' '}
            <button type="button" className="link-btn" disabled={busy} onClick={() => void breakGlass()}>
              {t('sso.break_glass')}
            </button>
          </p>
        )}
      </div>
    </section>
  );
}

/** /sso/first-factor?return=…：SSO-18，設第一把 passkey（plan SPEC #vms-web-sso-flows「SSO 登入的範圍」）。 */
export function SsoFirstFactorPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { session } = useApp();
  const [q] = useSearchParams();
  const back = safeReturn(q.get('return'));
  const verified = q.get('verified') === '1';
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const need = needsFirstFactor(session?.user);
  async function setup() {
    setBusy(true);
    setErr(null);
    try {
      await setupFirstPasskey(t('sso.ff_passkey_name'));
      nav(back);
    } catch (x) {
      const c = codeOf(x);
      if (c === 'REAUTH_REQUIRED') {
        // 登入超過 5 分鐘：先用公司帳號再驗證一次（identity 認 5 分鐘內的本組織 SSO 證明），回來再按一次（建立 passkey 要使用者自己按）
        try {
          window.location.assign(await ssoBegin(ssoScope(), 'step_up', '/sso/first-factor?verified=1&return=' + encodeURIComponent(back)));
          return;
        } catch (y) {
          setErr(codeOf(y));
        }
      } else {
        setErr(c);
      }
      setBusy(false);
    }
  }
  return (
    <section className="page" data-testid="sso-first-factor">
      <div className="card" style={{ maxWidth: 560 }}>
        <h1 className="section-title">{t('sso.ff_title')}</h1>
        {!need ? (
          <p data-testid="sso-ff-done">{t('sso.ff_not_needed')}</p>
        ) : (
          <>
            <p>{t('sso.ff_body')}</p>
            <p className="small muted">{t('sso.ff_why')}</p>
            {verified && <p className="notice small">{t('sso.ff_verified')}</p>}
            {!passkeySupported() && (
              <p className="error" role="alert" data-testid="sso-ff-unsupported">
                {t('sso.ff_unsupported')}
              </p>
            )}
            {err && (
              <div className="error" role="alert" data-sso-error={err}>
                {t(`passkey.err_${err}`, { defaultValue: t('sso.err_default', { code: err }) })}
              </div>
            )}
          </>
        )}
        <div className="row-end">
          <Link className="btn" to={need ? '/' : back}>
            {need ? t('sso.ff_later') : t('common.back')}
          </Link>
          {need && (
            <button className="btn btn-primary" type="button" disabled={busy || !passkeySupported()} onClick={() => void setup()} data-testid="sso-ff-setup">
              {t('sso.ff_setup')}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
